import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { formatDuration, formatViews } from "@/lib/movies";
import FavoriteButton from "@/components/feature/FavoriteButton";

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || movies.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % movies.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [paused, movies.length]);

  if (!movies.length) return null;

  const current = movies[active];
  const goPrev = () => setActive((prev) => (prev - 1 + movies.length) % movies.length);
  const goNext = () => setActive((prev) => (prev + 1) % movies.length);

  return (
    <section
      className="relative h-[560px] w-full overflow-hidden bg-background-950 md:h-[640px] lg:h-[700px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="热门影片焦点推荐"
    >
      {movies.map((movie, index) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={movie.backdrop || movie.poster}
            alt={`${movie.title}剧照`}
            title={`${movie.title} 在线观看 - 免费看大片`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-background-50/40" />
        </div>
      ))}

      <div className="relative z-20 mx-auto flex h-full w-full max-w-[1440px] flex-col justify-end px-4 pb-14 md:px-8 md:pb-20">
        <div key={current.id} className="max-w-2xl animate-fade-up">
          <h1 className="font-heading text-[11px] uppercase tracking-[0.42em] text-foreground-400">
            免费看大片
          </h1>

          <p className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 font-medium text-background-50">
              <i className="ri-fire-fill text-[11px]" />
              本周焦点
            </span>
            <span className="rounded-full border border-background-300/80 bg-background-100/60 px-2.5 py-1 text-foreground-300 backdrop-blur-sm">
              {current.genres.slice(0, 2).join(" · ")}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-background-300/80 bg-background-100/60 px-2.5 py-1 text-accent-300 backdrop-blur-sm">
              <i className="ri-star-fill text-[11px]" />
              {current.rating.toFixed(1)}
            </span>
          </p>

          <p className="mt-4 font-heading text-3xl font-semibold leading-tight text-foreground-50 md:text-5xl">
            {current.title}
          </p>
          <p className="mt-3 text-sm text-foreground-300 md:text-base">{current.tagline}</p>
          <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-foreground-400 md:line-clamp-3">
            {current.synopsis}
          </p>

          <p className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500 md:text-sm">
            <span>{current.year}</span>
            <span className="text-foreground-700">·</span>
            <span>{current.region}</span>
            <span className="text-foreground-700">·</span>
            <span>{formatDuration(current.duration)}</span>
            <span className="text-foreground-700">·</span>
            <span>导演 {current.director}</span>
            <span className="text-foreground-700">·</span>
            <span>{formatViews(current.views)}次播放</span>
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-fill text-base" />
              立即观看
            </Link>
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100/50 px-5 py-2.5 text-sm text-foreground-100 backdrop-blur-sm transition-colors hover:border-accent-500/60 hover:text-accent-300"
            >
              查看详情
              <i className="ri-arrow-right-line text-sm" />
            </Link>
            <FavoriteButton movieId={current.id} variant="compact" className="backdrop-blur-sm" />
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {movies.map((movie, index) => (
              <button
                key={movie.id}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`切换到第${index + 1}部影片：${movie.title}`}
                aria-current={index === active}
                className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                  index === active
                    ? "w-8 bg-accent-500"
                    : "w-4 bg-foreground-600/50 hover:bg-foreground-400"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="mr-1 font-heading text-xs tracking-widest text-foreground-500">
              {String(active + 1).padStart(2, "0")} / {String(movies.length).padStart(2, "0")}
            </span>
            <button
              type="button"
              onClick={goPrev}
              aria-label="上一部影片"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-100/60 text-foreground-200 backdrop-blur-sm transition-colors hover:border-primary-500/60 hover:text-primary-300"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="下一部影片"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-100/60 text-foreground-200 backdrop-blur-sm transition-colors hover:border-primary-500/60 hover:text-primary-300"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}